import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileText, CheckCircle, Loader2, AlertCircle, Clock, Eye, Send } from 'lucide-react';

export default function FormsPage() {
  const { api } = useAuth();
  const [myForms, setMyForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchMyForms(); }, []);

  const fetchMyForms = async () => {
    try {
      const r = await api.get('/forms/manage/my-forms/');
      setMyForms(r.data);
    } catch {
      // Fallback: fetch all active templates as available forms
      try {
        const r = await api.get('/forms/manage/');
        const templates = r.data?.results || r.data || [];
        setMyForms(templates.map(t => ({
          assignment_id: null,
          template_id: t.id,
          template_name: t.name,
          form_type: t.form_type,
          description: t.description,
          fields: t.fields || [],
          required: t.required,
          learner_name: null,
          learner_id: null,
          status: 'pending',
        })));
      } catch {}
    } finally { setLoading(false); }
  };

  const openForm = (form) => {
    setActiveForm(form);
    setResponses({});
    setFormOpen(true);
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const submitForm = async () => {
    // Validate required fields
    const fields = activeForm.fields || [];
    for (const field of fields) {
      if (field.required && !responses[field.id || field.label]) {
        toast.error(`Please fill in: ${field.label}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post('/forms/manage/submit/', {
        assignment_id: activeForm.assignment_id,
        template_id: activeForm.template_id,
        learner_id: activeForm.learner_id,
        responses,
      });
      toast.success('Form submitted successfully!');
      setFormOpen(false);
      fetchMyForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally { setSubmitting(false); }
  };

  const pendingForms = myForms.filter(f => f.status === 'pending');
  const completedForms = myForms.filter(f => f.status === 'completed');

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6" /> Forms & Waivers
        </h1>
        <p className="text-muted-foreground">View and complete required forms for your learners</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{myForms.length}</p>
          <p className="text-xs text-muted-foreground">Total Forms</p>
        </CardContent></Card>
        <Card className="border-yellow-200"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{pendingForms.length}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </CardContent></Card>
        <Card className="border-green-200"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{completedForms.length}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </CardContent></Card>
      </div>

      {/* Pending Forms */}
      {pendingForms.length > 0 && (
        <div>
          <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" /> Pending ({pendingForms.length})
          </h2>
          <div className="space-y-3">
            {pendingForms.map((form, i) => (
              <Card key={i} className="border-l-4 border-l-yellow-400">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{form.template_name}</h3>
                      {form.required && <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>}
                      <Badge variant="outline" className="text-xs">{form.form_type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{form.description}</p>
                    {form.learner_name && (
                      <p className="text-xs text-blue-600 mt-1">For: {form.learner_name}</p>
                    )}
                  </div>
                  <Button onClick={() => openForm(form)} className="bg-blue-600 hover:bg-blue-700">
                    <Eye className="h-4 w-4 mr-2" /> View & Sign
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Completed Forms */}
      {completedForms.length > 0 && (
        <div>
          <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" /> Completed ({completedForms.length})
          </h2>
          <div className="space-y-2">
            {completedForms.map((form, i) => (
              <Card key={i} className="border-l-4 border-l-green-400 opacity-80">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <h3 className="font-medium">{form.template_name}</h3>
                      <Badge variant="outline" className="text-xs">{form.form_type}</Badge>
                    </div>
                    {form.learner_name && <p className="text-xs text-muted-foreground ml-6">For: {form.learner_name}</p>}
                  </div>
                  <Badge className="bg-green-100 text-green-700">Signed</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {myForms.length === 0 && (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-semibold">No forms assigned</p>
          <p className="text-sm">You're all caught up! New forms will appear here when assigned.</p>
        </CardContent></Card>
      )}

      {/* FORM VIEWING & SIGNING DIALOG */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {activeForm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" /> {activeForm.template_name}
                </DialogTitle>
                {activeForm.learner_name && (
                  <p className="text-sm text-blue-600">For: {activeForm.learner_name}</p>
                )}
              </DialogHeader>

              {activeForm.description && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  {activeForm.description}
                </div>
              )}

              <div className="space-y-4 py-2">
                {(activeForm.fields || []).length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <p>This form has no fields to fill out.</p>
                    <p className="text-sm">Click "Sign & Submit" to acknowledge and complete this form.</p>
                  </div>
                ) : (
                  (activeForm.fields || []).map((field, i) => {
                    const fieldId = field.id || field.label;
                    return (
                      <div key={i} className="space-y-1.5">
                        <Label className="flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </Label>

                        {field.type === 'text' && (
                          <Input
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                            placeholder={field.placeholder || ''}
                          />
                        )}

                        {field.type === 'email' && (
                          <Input type="email"
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                            placeholder={field.placeholder || 'email@example.com'}
                          />
                        )}

                        {field.type === 'phone' && (
                          <Input type="tel"
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                            placeholder={field.placeholder || '(555) 123-4567'}
                          />
                        )}

                        {field.type === 'number' && (
                          <Input type="number"
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                          />
                        )}

                        {field.type === 'textarea' && (
                          <Textarea
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                            placeholder={field.placeholder || ''}
                            rows={3}
                          />
                        )}

                        {field.type === 'date' && (
                          <Input type="date"
                            value={responses[fieldId] || ''}
                            onChange={e => updateResponse(fieldId, e.target.value)}
                          />
                        )}

                        {field.type === 'checkbox' && (
                          <div className="flex items-center gap-2">
                            <input type="checkbox"
                              checked={responses[fieldId] === true}
                              onChange={e => updateResponse(fieldId, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm">{field.placeholder || 'I agree'}</span>
                          </div>
                        )}

                        {field.type === 'select' && (
                          <Select value={responses[fieldId] || ''} onValueChange={v => updateResponse(fieldId, v)}>
                            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map((opt, j) => (
                                <SelectItem key={j} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Acknowledgment */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-start gap-2">
                    <input type="checkbox"
                      checked={responses['_acknowledged'] === true}
                      onChange={e => updateResponse('_acknowledged', e.target.checked)}
                      className="rounded mt-1"
                    />
                    <p className="text-sm">
                      I acknowledge that I have read and understand the contents of this form. I agree to the terms stated above.
                    </p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                <Button onClick={submitForm} disabled={submitting || !responses['_acknowledged']}
                  className="bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Sign & Submit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
